import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { toast } from '../components/Toast'
import Spinner from '../components/Spinner'

type ReviewOut = {
  id: number
  user_id: number
  rating: number
  content: string
  created_at: string
}

function StarSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="text-3xl transition-transform hover:scale-110 focus:outline-none"
        >
          <span className={(hovered || value) >= star ? 'text-[var(--color-accent)]' : 'text-[var(--color-border-default)]'}>★</span>
        </button>
      ))}
    </div>
  )
}

const RATING_LABELS: Record<number, string> = {
  1: '별로예요',
  2: '아쉬워요',
  3: '보통이에요',
  4: '좋아요',
  5: '최고예요!',
}

export default function WriteReview() {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [rating, setRating] = useState(5)
  const [content, setContent] = useState('')

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
      return
    }
    loadMyReview()
  }, [isAuthenticated])

  const loadMyReview = async () => {
    setLoading(true)
    try {
      const { data } = await client.get<ReviewOut[]>('/reviews')
      const mine = data.find((r) => r.user_id === user?.id)
      if (mine) {
        setIsEdit(true)
        setRating(mine.rating)
        setContent(mine.content)
      }
    } catch {
      // 목록 조회 실패 시 작성 모드로 진행
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast('후기 내용을 입력해주세요.', 'error')
      return
    }
    setSubmitting(true)
    try {
      if (isEdit) {
        await client.put('/reviews', { rating, content: content.trim() })
        toast('후기가 수정되었습니다.', 'success')
      } else {
        await client.post('/reviews', { rating, content: content.trim() })
        toast('후기가 등록되었습니다.', 'success')
      }
      navigate('/profile')
    } catch (e: any) {
      toast(e?.response?.data?.detail || '후기 등록에 실패했습니다.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls = 'w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-4 py-2.5 text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)] transition-colors text-sm resize-none'

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto space-y-4">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
              {isEdit ? '후기 수정' : '후기 작성'}
            </h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">사주풀이 서비스 이용 후기를 남겨주세요</p>
          </div>
        </div>

        {/* 별점 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <h2 className="text-[var(--color-text-primary)] font-semibold mb-4">⭐ 별점 선택</h2>
          <div className="flex flex-col items-center gap-2">
            <StarSelector value={rating} onChange={setRating} />
            <span className="text-[var(--color-accent)] font-medium text-sm">
              {RATING_LABELS[rating] ?? ''}
            </span>
          </div>
        </div>

        {/* 내용 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <h2 className="text-[var(--color-text-primary)] font-semibold mb-4">✏️ 후기 내용</h2>
          <textarea
            className={inputCls}
            rows={5}
            placeholder="서비스를 이용한 소감을 자유롭게 작성해 주세요."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={500}
          />
          <p className="text-right text-xs text-[var(--color-text-disabled)] mt-1">{content.length} / 500</p>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex-1 py-3 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors"
          >
            랜딩으로
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity"
          >
            {submitting ? '등록 중...' : isEdit ? '수정 완료' : '후기 등록'}
          </button>
        </div>
      </div>
    </div>
  )
}
